// Maps raw wheel controls to driving inputs: which axis is steering and each
// pedal (with its rest and fully-pressed readings), which button does what,
// and the measured force direction. Saved per device in localStorage.

import type { RawControls } from './hidReport';
import { PRODUCT } from './g27';
import { wheelParams } from './settings';

export const GEAR_ACTIONS = ['gear1', 'gear2', 'gear3', 'gear4', 'gear5', 'gear6', 'gear7', 'reverse'] as const;
export type GearAction = (typeof GEAR_ACTIONS)[number];
export type Action = 'upshift' | 'downshift' | GearAction | 'select' | 'pause' | 'box' | 'reset' | 'camera';

export const ACTION_LABELS: Record<Action, string> = {
  upshift: 'Upshift (right paddle)',
  downshift: 'Downshift (left paddle)',
  gear1: 'Shifter 1st',
  gear2: 'Shifter 2nd',
  gear3: 'Shifter 3rd',
  gear4: 'Shifter 4th',
  gear5: 'Shifter 5th',
  gear6: 'Shifter 6th',
  gear7: 'Shifter 7th',
  reverse: 'Shifter reverse',
  select: 'Menu select (OK)',
  pause: 'Pause / back',
  box: 'Box this lap',
  reset: 'Reset car to track',
  camera: 'Horizon mode',
};

export interface AxisBinding {
  axis: number;
  /** Raw 0..1 reading at rest (pedal up; wheel centred). */
  rest: number;
  /** Raw reading fully pressed (pedal); at full right lock (wheel). */
  full: number;
}

export type PedalRole = 'throttle' | 'brake' | 'clutch';

export interface WheelProfile {
  steer: AxisBinding;
  throttle?: AxisBinding;
  brake?: AxisBinding;
  /** null = deliberately unbound (saved, so the default doesn't come back on reload). */
  clutch?: AxisBinding | null;
  buttons: Partial<Record<Action, number>>;
  /**
   * Multiplies the game's torque (+ = turn the wheel right) into the wheel's
   * own force convention. Measured by the setup screen's direction test.
   */
  forceSign: 1 | -1;
  forceSignChecked: boolean;
}

/**
 * Defaults per wheel (native mode). Axes are the same on the G27 and G29: the
 * wheel, then accelerator, brake and clutch, all reading high when released.
 * Buttons are in report order (per sonik-br/lgff_wheel_adapter's report structs):
 *
 * - G27: 0-3 shifter face buttons, 4/5 right/left paddle, 6/7 the rim's top
 *   right/left buttons, 12-17 shifter gates 1-6, and reverse is the extra
 *   button 22 (see hidWheel.ts).
 * - G29: 0-3 ✕ □ ○ △, 4/5 right/left paddle (R1/L1), 8 Share, 9 Options,
 *   12-17 Driving Force Shifter gates 1-6, 18 reverse.
 *
 * Everything the game needs is on the rim and paddles; the guided setup confirms it.
 */
export function defaultProfile(productId?: number): WheelProfile {
  const gears = { gear1: 12, gear2: 13, gear3: 14, gear4: 15, gear5: 16, gear6: 17 };
  const buttons: WheelProfile['buttons'] =
    productId === PRODUCT.G29
      ? { upshift: 4, downshift: 5, select: 0, pause: 9, box: 1, reset: 3, camera: 8, ...gears, reverse: 18 }
      : { upshift: 4, downshift: 5, select: 6, pause: 7, ...gears, reverse: 22 };
  return {
    steer: { axis: 0, rest: 0.5, full: 1 },
    throttle: { axis: 1, rest: 1, full: 0 },
    brake: { axis: 2, rest: 1, full: 0 },
    clutch: { axis: 3, rest: 1, full: 0 },
    buttons,
    forceSign: -1,
    forceSignChecked: false,
  };
}

const STORAGE_PREFIX = 'fone.wheel.';

export function loadProfile(key: string, defaults: WheelProfile = defaultProfile()): WheelProfile {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw) return { ...defaults, ...(JSON.parse(raw) as Partial<WheelProfile>) };
  } catch {
    // Fall through to defaults.
  }
  return defaults;
}

export function saveProfile(key: string, profile: WheelProfile): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(profile));
  } catch {
    // Not persisted; works for this session.
  }
}

/** Pedal travel 0..1 with dead zones at both ends. */
export function pedalValue(controls: RawControls, b: AxisBinding | undefined | null, deadZone = wheelParams.pedalDeadZone.value): number {
  if (!b) return 0;
  const raw = controls.axes[b.axis];
  if (raw === undefined || b.full === b.rest) return 0;
  const t = (raw - b.rest) / (b.full - b.rest);
  return Math.min(1, Math.max(0, (t - deadZone) / (1 - 2 * deadZone)));
}

/** Steering −1..1 (+ right), where ±1 is the bound full lock. */
export function steerValue(controls: RawControls, b: AxisBinding): number {
  const raw = controls.axes[b.axis];
  if (raw === undefined || b.full === b.rest) return 0;
  return Math.min(1, Math.max(-1, (raw - b.rest) / (b.full - b.rest)));
}

export interface WheelInputState {
  steer: number;
  throttle: number;
  brake: number;
  clutch: number;
  /** H-shifter position when gear buttons are bound: −1 reverse, 0 neutral, 1..7. */
  shifter: number | undefined;
  /** Buttons pressed since the last read. */
  pressed: Action[];
}

/** Applies a profile to raw controls, with button edge detection. */
export class WheelInput {
  private prev: boolean[] = [];

  constructor(public profile: WheelProfile) {}

  read(c: RawControls): WheelInputState {
    const p = this.profile;
    const pressed: Action[] = [];
    let shifterBound = false;
    let shifter = 0;
    for (const [action, index] of Object.entries(p.buttons) as Array<[Action, number]>) {
      const down = !!c.buttons[index];
      if (down && !this.prev[index]) pressed.push(action);
      if ((GEAR_ACTIONS as readonly string[]).includes(action)) {
        shifterBound = true;
        if (down) shifter = action === 'reverse' ? -1 : Number(action.slice(4));
      }
    }
    this.prev = [...c.buttons];
    const brakeRaw = pedalValue(c, p.brake);
    return {
      steer: steerValue(c, p.steer),
      throttle: pedalValue(c, p.throttle),
      brake: Math.pow(brakeRaw, wheelParams.brakeGamma.value),
      clutch: pedalValue(c, p.clutch),
      shifter: shifterBound ? shifter : undefined,
      pressed,
    };
  }
}

export type LearnResult = { kind: 'axis'; axis: number; rest: number; full: number } | { kind: 'button'; button: number };

/**
 * Learn a control by watching for it to move: snapshot the controls, then
 * report the first button pressed, or the axis that moved furthest once it
 * comes back (so a pedal's full travel is captured in one press-and-release).
 */
export class Learner {
  private readonly baseAxes: number[];
  private readonly baseButtons: boolean[];
  private peakAxis = -1;
  private peak = 0;
  private peakValue = 0;

  constructor(
    base: RawControls,
    readonly want: 'axis' | 'button',
    /** Axes to ignore (e.g. the wheel while learning pedals). */
    private readonly ignoreAxes: number[] = [],
  ) {
    this.baseAxes = [...base.axes];
    this.baseButtons = [...base.buttons];
  }

  /** How far the leading axis has moved, 0..1 (progress indicator). */
  get progress(): number {
    return Math.min(1, this.peak / 0.5);
  }

  update(c: RawControls): LearnResult | undefined {
    if (this.want === 'button') {
      for (let i = 0; i < c.buttons.length; i++) {
        if (c.buttons[i] && !this.baseButtons[i]) return { kind: 'button', button: i };
      }
      return undefined;
    }
    for (let i = 0; i < c.axes.length; i++) {
      if (this.ignoreAxes.includes(i)) continue;
      const d = Math.abs((c.axes[i] ?? 0) - (this.baseAxes[i] ?? 0));
      if (d > this.peak) {
        this.peak = d;
        this.peakAxis = i;
        this.peakValue = c.axes[i] ?? 0;
      }
    }
    if (this.peakAxis < 0 || this.peak < 0.3) return undefined;
    const now = Math.abs((c.axes[this.peakAxis] ?? 0) - (this.baseAxes[this.peakAxis] ?? 0));
    // Done once the control is back near where it started.
    if (now < this.peak * 0.25) return { kind: 'axis', axis: this.peakAxis, rest: this.baseAxes[this.peakAxis] ?? 0, full: this.peakValue };
    return undefined;
  }
}
